import { useMemo } from 'react';
import { Button, Colors, Icon } from '@mercator-gcp/grout';
import { COLUMN_LABELS } from 'components/elements/LeadsTable/constants';
import MultiSelect from 'components/elements/MultiSelect';

const cogButtonStyle = {
  minWidth: 'auto',
  padding: 'padding_4',
  background: 'transparent',
  borderRadius: '50%',
  '&:hover, &:focus': {
    background: Colors.ArmyGreen100,
    boxShadow: 'none',
  },
};

type Props = {
  /** Toggleable column ids in display order (ACTION excluded by the caller). */
  manageableColumnIds: string[];
  /** Currently visible column ids. */
  visibleColumns: Set<string>;
  onChangeSelection: (selectedKeys: string[]) => void;
  onClose: () => void;
};

/**
 * Cog-triggered popover that lets the user toggle which leads-table columns are
 * shown. Selecting a column updates the table immediately; closing the popover
 * persists the selection (handled by the parent via onClose).
 */
const ColumnSelector = ({
  manageableColumnIds,
  visibleColumns,
  onChangeSelection,
  onClose,
}: Props) => {
  const options = useMemo(
    () =>
      manageableColumnIds.map((id) => ({
        key: id,
        label: COLUMN_LABELS[id] ?? id,
      })),
    [manageableColumnIds],
  );

  const selectedKeys = useMemo(
    () => manageableColumnIds.filter((id) => visibleColumns.has(id)),
    [manageableColumnIds, visibleColumns],
  );

  return (
    <MultiSelect
      options={options}
      selectedKeys={selectedKeys}
      onChangeSelection={onChangeSelection}
      onClose={onClose}
      menuMinWidth={240}
      placement="left-start"
      bulkAction="selectAll"
      renderTrigger={() => (
        <Button variant="unstyled" sx={cogButtonStyle} aria-label="Select columns">
          <Icon name={Icon.Name.Gear} fill={Colors.ArmyGreen900} />
        </Button>
      )}
    />
  );
};

export default ColumnSelector;
